import { io } from "socket.io-client";

// ─────────────────────────────────────────────────────────────
// SERVER URL
//   1) If REACT_APP_SERVER_URL is set (client/.env), use that
//      — needed when the server is deployed online (Render/Railway).
//   2) Otherwise use the SAME host the page was opened from, on
//      port 5000 — makes LAN use (two devices on one WiFi) work.
// ─────────────────────────────────────────────────────────────
export const SERVER_URL =
  process.env.REACT_APP_SERVER_URL ||
  window.location.origin;

// ─────────────────────────────────────────────────────────────
// LOGIN TOKEN
// The server gives us a token when we log in / register. We send it with
// every API call and with the socket connection, so the server always knows
// exactly WHICH ACCOUNT is talking — that is what makes messages arrive in
// the right person's account and show the right sender name.
// ─────────────────────────────────────────────────────────────
const TOKEN_KEY = "mc3_token";
export const getToken   = () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
export const setToken   = (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch {} };
export const clearToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch {} };

// Pre-launch gate token (given after both passwords + captchas are passed)
const GATE_KEY = "mc3_gate";
// Kept in sessionStorage on purpose: the pass is forgotten when the tab/browser is closed, so the
// password page appears again on every new visit (a plain refresh in the same tab stays unlocked).
export const getGate   = () => { try { return sessionStorage.getItem(GATE_KEY) || ""; } catch { return ""; } };
export const setGate   = (t) => { try { sessionStorage.setItem(GATE_KEY, t); } catch {} };
export const clearGate = () => { try { sessionStorage.removeItem(GATE_KEY); } catch {} };

// Trusted-device token: given once the Telegram approval has succeeded. Unlike the gate pass it lives in
// localStorage, so later visits only need password + captcha (no Telegram approval again).
const DEVICE_KEY = "mc3_device";
export const getDevice   = () => { try { return localStorage.getItem(DEVICE_KEY) || ""; } catch { return ""; } };
export const setDevice   = (t) => { try { localStorage.setItem(DEVICE_KEY, t); } catch {} };
export const clearDevice = () => { try { localStorage.removeItem(DEVICE_KEY); } catch {} };

// Small fetch wrapper: JSON in/out, adds the token, throws Error(message) on failure.
export async function api(path, { method = "GET", body } = {}) {
  const token = getToken();
  let res;
  try {
    res = await fetch(SERVER_URL + path, {
      method,
      headers: { "Content-Type": "application/json", "X-Gate-Token": getGate(), ...(token ? { Authorization: "Bearer " + token } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Can't reach the server. Check your connection and try again.");
  }
  let data = {};
  try { data = await res.json(); } catch {}
  if (res.status === 401 && data.gate) window.dispatchEvent(new Event("mc3-gate-required")); // gate token missing/expired
  if (res.status === 401 && token && !data.gate && !path.startsWith("/api/login")) {
    window.dispatchEvent(new Event("mc3-unauthorized")); // session expired → App sends user to login
  }
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

export const socket = io(SERVER_URL, {
  autoConnect: false,                       // only connect once someone is logged in
  auth: (cb) => cb({ token: getToken(), gate: getGate() }),  // re-read the token on every (re)connect
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  transports: ["websocket", "polling"],
});

export const connectSocket = () => { if (!socket.connected) socket.connect(); };
export const disconnectSocket = () => socket.disconnect();

socket.on("connect", () => console.log("✅ Messenger connected"));
socket.on("disconnect", () => console.log("⚠️ Messenger disconnected"));
socket.on("connect_error", (e) => {
  console.log("❌ Error:", e.message);
  if (e.message === "unauthorized") { window.dispatchEvent(new Event("mc3-unauthorized")); window.dispatchEvent(new Event("mc3-gate-check")); }
});

// ─────────────────────────────────────────────────────────────
// FILE UPLOAD — streams the raw file to the server (no base64, no memory spike), so 600 MB works.
// Returns { promise, abort }. promise resolves with { file:{ id, name, size, mime } }.
// ─────────────────────────────────────────────────────────────
export const MAX_FILE_BYTES = 600 * 1024 * 1024;   // keep equal to MAX_FILE_MB on the server (default 600)

export function uploadFile(file, onProgress) {
  const xhr = new XMLHttpRequest();
  const promise = new Promise((resolve, reject) => {
    xhr.open("PUT", `${SERVER_URL}/api/upload?name=${encodeURIComponent(file.name)}&type=${encodeURIComponent(file.type || "application/octet-stream")}`);
    xhr.setRequestHeader("Authorization", "Bearer " + getToken());
    xhr.setRequestHeader("X-Gate-Token", getGate());
    xhr.setRequestHeader("Content-Type", "application/octet-stream");
    xhr.timeout = 0; // no client-side time limit — big files on slow connections need as long as they need
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress?.(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let data = {};
      try { data = JSON.parse(xhr.responseText); } catch {}
      if (xhr.status >= 200 && xhr.status < 300 && data.file) return resolve(data);
      if (xhr.status === 401) window.dispatchEvent(new Event("mc3-unauthorized"));
      reject(new Error(data.error || `Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload — check your connection and retry"));
    xhr.onabort = () => reject(new Error("Upload cancelled"));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

// Files live on the server and are referenced in messages as "/files/<id>". Turn that into a
// loadable URL (the server checks the logged-in user may open it). Inline/blob URLs pass through.
export const mediaSrc = (url, { download = false } = {}) => {
  if (!url || !url.startsWith("/files/")) return url;
  return `${SERVER_URL}${url}?t=${encodeURIComponent(getToken() || "")}&g=${encodeURIComponent(getGate())}${download ? "&dl=1" : ""}`;
};